// Edit sheet for item_optics_attributes (P1-18 PR B).

import { validateOpticsAttributes } from "@betterpos/domain";
import { useState } from "react";
import { readSessionToken } from "../../data/audit.ts";
import type { OpticsAttributes } from "../../data/catalog-attrs.ts";
import { type OpticsAttributesUpdate, updateItemOptics } from "../../data/catalog-edits.ts";
import { AttrSheet, messageForEditError } from "./AttrSheet.tsx";
import { Field, TextInput, Toggle } from "./SheetControls.tsx";

type Props = {
  itemId: string;
  current: OpticsAttributes;
  onSaved: (next: OpticsAttributesUpdate) => void;
  onCancel: () => void;
};

export function OpticsAttrsSheet({ itemId, current, onSaved, onCancel }: Props) {
  const [manufacturer, setManufacturer] = useState(current.manufacturer);
  const [model, setModel] = useState(current.model);
  const [magnification, setMagnification] = useState(current.magnification);
  const [reticle, setReticle] = useState(current.reticle ?? "");
  const [mount, setMount] = useState(current.mount ?? "");
  const [isIlluminated, setIsIlluminated] = useState(current.isIlluminated);
  const [batteryType, setBatteryType] = useState(current.batteryType ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidate = {
    manufacturer,
    model,
    magnification,
    reticle: reticle === "" ? null : reticle,
    mount: mount === "" ? null : mount,
    isIlluminated,
    batteryType: batteryType === "" ? null : batteryType,
  };
  const validation = validateOpticsAttributes(candidate);
  const changed =
    manufacturer.trim() !== current.manufacturer ||
    model.trim() !== current.model ||
    magnification.trim() !== current.magnification ||
    (reticle.trim() || null) !== current.reticle ||
    (mount.trim() || null) !== current.mount ||
    isIlluminated !== current.isIlluminated ||
    (batteryType.trim() || null) !== current.batteryType;
  const canSave = validation.ok && changed && !saving;

  const save = async () => {
    if (!validation.ok) return;
    const token = readSessionToken();
    if (!token) {
      setError("Not signed in.");
      return;
    }
    setSaving(true);
    setError(null);
    const res = await updateItemOptics(token, itemId, {
      expectedVersion: current.version,
      attributes: validation.value,
    });
    setSaving(false);
    if (!res.ok) {
      setError(messageForEditError(res, "This field"));
      return;
    }
    onSaved(res.value);
  };

  return (
    <AttrSheet
      title="Edit optics attributes"
      error={error}
      saving={saving}
      canSave={canSave}
      onCancel={onCancel}
      onSave={save}
    >
      <Field label="Manufacturer">
        <TextInput value={manufacturer} onChange={setManufacturer} disabled={saving} />
      </Field>
      <Field label="Model">
        <TextInput value={model} onChange={setModel} disabled={saving} />
      </Field>
      <Field label="Magnification">
        <TextInput
          value={magnification}
          onChange={setMagnification}
          disabled={saving}
          placeholder="e.g. 1-6x24 or 1x"
        />
      </Field>
      <Field label="Reticle">
        <TextInput
          value={reticle}
          onChange={setReticle}
          disabled={saving}
          placeholder="BDC / Mil-dot / Duplex"
        />
      </Field>
      <Field label="Mount">
        <TextInput
          value={mount}
          onChange={setMount}
          disabled={saving}
          placeholder="Picatinny / 30mm tube"
        />
      </Field>
      <div className="flex items-center justify-between">
        <span className="text-[15px] text-slate-700">Illuminated</span>
        <Toggle
          value={isIlluminated}
          onChange={setIsIlluminated}
          disabled={saving}
          label="Illuminated"
        />
      </div>
      <Field label="Battery">
        <TextInput
          value={batteryType}
          onChange={setBatteryType}
          disabled={saving}
          placeholder="CR2032"
        />
      </Field>
    </AttrSheet>
  );
}
